// https://github.com/colinhacks/zod/blob/62b7842c46dd7f1004f41e2220284abb75378fb7/src/locales/en.ts

import {
  ZodErrorMap,
  ZodIssueCode,
  ZodParsedType,
  util,
} from 'zod';

const errorMap: ZodErrorMap = (issue, _ctx) => {
  let message: string;
  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      if (issue.received === ZodParsedType.undefined) {
        message = 'Wymagane';
      } else {
        message = `Oczekiwano ${issue.expected}, otrzymano ${issue.received}`;
      }
      break;
    case ZodIssueCode.invalid_literal:
      message = `Nieprawidłowa wartość literału, oczekiwano ${JSON.stringify(
        issue.expected,
        util.jsonStringifyReplacer,
      )}`;
      break;
    case ZodIssueCode.unrecognized_keys:
      message = `Nierozpoznany/e klucz(e) w obiekcie: ${util.joinValues(
        issue.keys,
        ', ',
      )}`;
      break;
    case ZodIssueCode.invalid_union:
      message = 'Nieprawidłowe dane wejściowe';
      break;
    case ZodIssueCode.invalid_union_discriminator:
      message = `Nieprawidłowa wartość dyskryminatora. Oczekiwano ${util.joinValues(
        issue.options,
      )}`;
      break;
    case ZodIssueCode.invalid_enum_value:
      message = `Nieprawidłowa wartość wyliczenia. Oczekiwano ${util.joinValues(
        issue.options,
      )}, otrzymano '${issue.received}'`;
      break;
    case ZodIssueCode.invalid_arguments:
      message = 'Nieprawidłowe argumenty funkcji';
      break;
    case ZodIssueCode.invalid_return_type:
      message = 'Nieprawidłowy typ zwracany przez funkcję';
      break;
    case ZodIssueCode.invalid_date:
      message = 'Nieprawidłowa data';
      break;
    case ZodIssueCode.invalid_string:
      if (typeof issue.validation === 'object') {
        if ('includes' in issue.validation) {
          message = `Nieprawidłowe dane: musi zawierać "${issue.validation.includes}"`;

          if (typeof issue.validation.position === 'number') {
            message = `${message} na jednej lub więcej pozycjach większych lub równych ${issue.validation.position}`;
          }
        } else if ('startsWith' in issue.validation) {
          message = `Nieprawidłowe dane: musi zaczynać się od "${issue.validation.startsWith}"`;
        } else if ('endsWith' in issue.validation) {
          message = `Nieprawidłowe dane: musi kończyć się na "${issue.validation.endsWith}"`;
        } else {
          util.assertNever(issue.validation);
        }
      } else if (issue.validation !== 'regex') {
        message = `Nieprawidłowe ${issue.validation}`;
      } else {
        message = 'Nieprawidłowe';
      }
      break;
    case ZodIssueCode.too_small:
      if (issue.type === 'array') {
        message = `Tablica musi zawierać ${
          issue.exact ? 'dokładnie' : issue.inclusive ? 'przynajmniej' : 'więcej niż'
        } ${issue.minimum} element(ów)`;
      } else if (issue.type === 'string') {
        if (issue.inclusive && issue.minimum === 1) {
          message = 'Nie może być puste';
        } else {
          message = `Musi zawierać ${
            issue.exact ? 'dokładnie' : issue.inclusive ? 'przynajmniej' : 'więcej niż'
          } ${issue.minimum} znaków`;
        }
      } else if (issue.type === 'number') {
        message = `Liczba musi być ${
          issue.exact
            ? 'dokładnie równa '
            : issue.inclusive
              ? 'większa lub równa '
              : 'większa niż '
        }${issue.minimum}`;
      } else if (issue.type === 'date') {
        message = `Data musi być ${
          issue.exact
            ? 'dokładnie równa '
            : issue.inclusive
              ? 'późniejsza lub równa '
              : 'późniejsza niż '
        }${new Date(Number(issue.minimum))}`;
      } else {
        message = 'Nieprawidłowe dane';
      }
      break;
    case ZodIssueCode.too_big:
      if (issue.type === 'array') {
        message = `Tablica może zawierać ${
          issue.exact ? 'dokładnie' : issue.inclusive ? 'maksymalnie' : 'mniej niż'
        } ${issue.maximum} element(ów)`;
      } else if (issue.type === 'string') {
        message = `Ciąg znaków może zawierać ${
          issue.exact ? 'dokładnie' : issue.inclusive ? 'maksymalnie' : 'mniej niż'
        } ${issue.maximum} znaków`;
      } else if (issue.type === 'number') {
        message = `Liczba musi być ${
          issue.exact
            ? 'dokładnie'
            : issue.inclusive
              ? 'mniejsza lub równa'
              : 'mniejsza niż'
        } ${issue.maximum}`;
      } else if (issue.type === 'bigint') {
        message = `BigInt musi być ${
          issue.exact
            ? 'dokładnie'
            : issue.inclusive
              ? 'mniejszy lub równy'
              : 'mniejszy niż'
        } ${issue.maximum}`;
      } else if (issue.type === 'date') {
        message = `Data musi być ${
          issue.exact
            ? 'dokładnie'
            : issue.inclusive
              ? 'wcześniejsza lub równa'
              : 'wcześniejsza niż'
        } ${new Date(Number(issue.maximum))}`;
      } else {
        message = 'Nieprawidłowe dane';
      }
      break;
    case ZodIssueCode.custom:
      message = 'Nieprawidłowe dane';
      break;
    case ZodIssueCode.invalid_intersection_types:
      message = 'Wyniki przecięcia nie mogły zostać połączone';
      break;
    case ZodIssueCode.not_multiple_of:
      message = `Liczba musi być wielokrotnością ${issue.multipleOf}`;
      break;
    case ZodIssueCode.not_finite:
      message = 'Liczba musi być skończona';
      break;
    default:
      message = _ctx.defaultError;
      util.assertNever(issue);
  }
  return {
    message,
  };
};

export default errorMap;
